import { Injectable } from '@angular/core';
import { HttpClientService } from '@core/services/http-client/http-client.service';
import { API_URL_CONSTANT } from '@sharedModule/constants/api-url.constant';
import {
  CollectionPoint,
  DeliveryBank,
  DeliveryBranch,
  MobileNetworkOperatorsResponse,
  VelocityCheckPayload,
} from '@sharedModule/models/delivery.model';
import { ResponseIdentity } from '@sharedModule/models/server-response-identity.model';
import { Observable } from 'rxjs';

/**
 * Wraps the `transaction/*` delivery lookups required to build the conditional
 * transfer-detail groups (bank-transfer / cash-pickup / mobile-transfer) on the
 * recipient and create-transaction forms.
 */
@Injectable({
  providedIn: 'root',
})
export class DeliveryLookupService {
  constructor(private httpClient: HttpClientService) {}

  // --- mobile-transfer ---
  getMobileNetworkOperators(payload: {
    destination_country: string;
  }): Observable<ResponseIdentity<MobileNetworkOperatorsResponse>> {
    return this.httpClient.post(
      API_URL_CONSTANT.delivery.getMobileNetworkOperators,
      payload
    );
  }

  // --- bank-transfer ---
  getDeliveryBanks(payload: {
    destination_country: string;
  }): Observable<ResponseIdentity<DeliveryBank[]>> {
    return this.httpClient.post(
      API_URL_CONSTANT.delivery.getDeliveryBanks,
      payload
    );
  }

  getDeliveryBranchStates(payload: {
    delivery_bank_id: number;
  }): Observable<ResponseIdentity<string[]>> {
    return this.httpClient.post(
      API_URL_CONSTANT.delivery.getDeliveryBranchStates,
      payload
    );
  }

  getDeliveryBranchCities(payload: {
    delivery_bank_id: number;
  }): Observable<ResponseIdentity<string[]>> {
    return this.httpClient.post(
      API_URL_CONSTANT.delivery.getDeliveryBranchCities,
      payload
    );
  }

  getDeliveryBranches(payload: {
    delivery_bank_id: number;
  }): Observable<ResponseIdentity<DeliveryBranch[]>> {
    return this.httpClient.post(
      API_URL_CONSTANT.delivery.getDeliveryBranches,
      payload
    );
  }

  // --- cash-pickup ---
  getCollectionPointStates(payload: {
    destination_country: string;
  }): Observable<ResponseIdentity<string[]>> {
    return this.httpClient.post(
      API_URL_CONSTANT.delivery.getCollectionPointStates,
      payload
    );
  }

  getCollectionPointCities(payload: {
    destination_country: string;
    collection_point_state?: string;
  }): Observable<ResponseIdentity<string[]>> {
    return this.httpClient.post(
      API_URL_CONSTANT.delivery.getCollectionPointCities,
      payload
    );
  }

  getCollectionPoints(payload: {
    destination_country: string;
    collection_point_state?: string;
    collection_point_city?: string;
  }): Observable<ResponseIdentity<CollectionPoint[]>> {
    return this.httpClient.post(
      API_URL_CONSTANT.delivery.getCollectionPoints,
      payload
    );
  }

  // --- pre-transaction guard ---
  checkVelocityLimit(
    payload: VelocityCheckPayload
  ): Observable<ResponseIdentity<unknown>> {
    return this.httpClient.post(
      API_URL_CONSTANT.delivery.checkVelocityLimit,
      payload
    );
  }
}
